import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
	fetchTours,
	fetchSelectedTours,
	saveSelectedTournaments,
	fetchUpcomingToursWithStatus,
	fetchMyContractedTourIds,
} from '../services/api'
import { useAuthStatus } from '../utils'
import { useUser } from './user'

/**
 * 전체 투어 목록을 조회하는 훅
 * @param {string} gender - 성별 필터 (MALE, FEMALE) - 선택사항
 */
export const useTours = (gender) => {
	return useQuery({
		queryKey: ['tours', gender], // gender를 queryKey에 포함
		queryFn: () => fetchTours(gender),
		staleTime: 1000 * 60 * 10, // 10분간 캐시 유지 (투어 목록은 자주 변하지 않음)
		retry: 1,
	})
}

/**
 * 사용자가 선택한 투어 목록을 조회하는 훅
 * @param {string} userId - 사용자 ID
 */
export const useSelectedTours = (userId) => {
	const { data: currentUser } = useUser()
	const userIdToUse = userId || currentUser?.id || null
	return useQuery({
		queryKey: ['selected-tours', userIdToUse],
		queryFn: () => fetchSelectedTours(userIdToUse),
		enabled: !!userIdToUse,
		staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
		retry: 1,
	})
}

/**
 * 다가오는 대회 목록 + 라벨 (홈 화면용, 로그인 필수)
 */
export const useUpcomingToursWithStatus = (limit = 10) => {
	const [auth] = useAuthStatus()
	const token = auth.token

	return useQuery({
		queryKey: ['upcoming-tours-with-status', token, limit],
		queryFn: () => fetchUpcomingToursWithStatus(token, limit),
		enabled: !!token,
		staleTime: 0,
		refetchOnWindowFocus: true,
	})
}

/**
 * 로그인 사용자 기준 완전 체결(ACCEPTED) 대회 ID (제안·공고 UI에서 선택 비활성화)
 */
export const useMyContractedTourIds = () => {
	const [auth] = useAuthStatus()
	const token = auth.token

	return useQuery({
		queryKey: ['my-contracted-tour-ids', token],
		queryFn: () => fetchMyContractedTourIds(token),
		enabled: !!token,
		staleTime: 1000 * 60 * 2,
	})
}

/**
 * 선택한 투어 목록을 저장하는 mutation 훅
 */
export const useSaveSelectedTournaments = () => {
	const queryClient = useQueryClient()
	const [auth] = useAuthStatus()

	return useMutation({
		mutationFn: ({ tournamentIds, skipped = false }) =>
			saveSelectedTournaments(auth.token, tournamentIds, skipped),
		onSuccess: () => {
			// 저장 성공 시 선택된 투어 목록 캐시 무효화
			queryClient.invalidateQueries({ queryKey: ['selected-tours', auth.user?.id] })
			// 홈 다가오는 대회 라벨도 갱신
			queryClient.invalidateQueries({ queryKey: ['upcoming-tours-with-status'] })
		},
	})
}
